/**
 * Period selector for the UsageTab.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

import { PlanInvoice, TaskDetailList } from '@/pages/Billing/Invoice'
import { AccountUtil, TierType, Util } from '@amplify3/duplo-back-end'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import './UsagePeriodSelect.css'

/**
 * Return the name of the KPI period.
 *
 * @param kpi The Kpi instance.
 * @param tierType The type of tier.
 * @returns The name in string.
 */
export function getPeriodName(kpi: TaskDetailList, tierType?: TierType) {
  if (AccountUtil.isTierEnterprise(tierType)) {
    return kpi.periodStart.toLocaleDateString(Util.getLocale(), {
      year: 'numeric',
      month: 'long',
    })
  }

  return kpi.periodStart.toLocaleDateString(Util.getLocale(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export interface UsagePeriodProps {
  invoice?: PlanInvoice
  selectedIndex: number | undefined
  onChange: (event: SelectChangeEvent<number>) => void
  disabled?: boolean
}

/**
 * Customized Select component for the selection of the KPI period.
 */
export const UsagePeriodSelect = (props: UsagePeriodProps) => {
  function getIndex(
    history: TaskDetailList[] | undefined,
    requestedIndex: number | undefined
  ) {
    if (
      !history?.length ||
      requestedIndex == null ||
      requestedIndex >= history.length
    ) {
      // MUI default value is ''
      return ''
    }

    return requestedIndex
  }

  return (
    <Select
      className="date-selector"
      labelId="kpi-selector-label"
      id="kpi-selector"
      value={getIndex(
        props.invoice?.taskDetailHistoryList,
        props.selectedIndex
      )}
      onChange={props.onChange}
      inputProps={{ 'aria-label': 'Without label' }}
      disabled={props.disabled}
      MenuProps={{
        // Avoid shift of body to the left when opening the drop-down menu
        // due to the add of right-padding to compensate scroll bar appearance
        disableScrollLock: true,
        id: 'kpi-selector-menu',
      }}
    >
      {props.invoice?.taskDetailHistoryList.map(
        (kpi: TaskDetailList, index: number) => (
          <MenuItem key={kpi.periodStart.toISOString()} value={index}>
            {`${getPeriodName(kpi, props.invoice?.tier)} `}
          </MenuItem>
        )
      )}
    </Select>
  )
}

export default UsagePeriodSelect
